import { useRef } from "react";
import {
  TrendingUp, CircleAlert, ShoppingCart, MapPin, CreditCard, Clock, Menu, Signal, Wifi, BatteryFull,
  Plus, MousePointer2, Banknote, FileText, CheckCircle2,
} from "lucide-react";
import { publicAsset } from "../../../features/tenantPath";
import { useScenar, usePrelinanie, Pocitadlo, eura, kusy } from "./scenar";
import "./HeroMockup.scss";

/**
 * Ilustracny nahlad aplikacie v hero sekcii -- zjednodusena kopia skutocnych
 * obrazoviek (Prehlad v admin paneli -- OverviewSection -- a "Moje zavozy"
 * vodica), cisto v CSS, aby sa nemusel udrziavat screenshot. Mena a sumy su
 * vymyslene. Ked sa vzhlad tychto obrazoviek vyrazne zmeni, dolad aj toto.
 *
 * Nahlad sa prehrava ako kratky pribeh v slucke s jednou objednavkou (BOD):
 * manazer ju zapise, ta dostane trasu a vodicovi sa objavi v zozname; vodic ju
 * doruci, prijme hotovost a manazerovi sa trzba prepocita sama. Ziadne hlasky,
 * ktore aplikacia v skutocnosti nema -- obe obrazovky sa len obnovia.
 * Vyvoj riadi casovy plan KROKY (prehrava ho useScenar zo scenar.js).
 */

// [nazov kroku, cas od zaciatku kola v ms]
const KROKY = [
  ["start", 0],
  ["kurzor", 1200],    // kurzor manazera ide na "Nova objednavka"
  ["klik", 2100],
  ["nova", 2500],      // objednavka pribudne do zoznamu
  ["trasa", 3900],     // dostane trasu SEVER (prednastavenu u zakaznika)
  ["vodic", 4700],     // ... a vodicovi sa objavi v zozname
  ["tap", 6900],       // vodic tukne "Dorucit a zaplatit"
  ["platba", 7300],
  ["suma", 8000],      // prijata suma
  ["potvrd", 9000],
  ["dorucene", 9500],
  ["naDesktop", 10000], // na mobile sa z telefonu vrati na okno manazera
  ["trzba", 10400],    // manazerovi sa prepocita trzba
  ["koniec", 14600],   // posledny zaber -- do noveho kola sa prelne
];
const DLZKA_KOLA = 15200;

// polozka: [pocet, kod, zmenene?] -- zmenena polozka kratko zablikne
const Jedla = ({ polozky }) => (
  <span className="hero-mock__meals">
    {polozky.map(([pocet, kod, zmena]) => (
      <span key={kod} className={zmena ? "hero-mock__meal--zmena" : undefined}>
        {pocet}<b>{kod}</b>
      </span>
    ))}
  </span>
);

/** Obal, ktory obsah plynulo rozbali / zbali (grid-template-rows 0fr <-> 1fr). */
const Skladacka = ({ otvorena, className = "", children }) => (
  <div className={`hero-mock__fold ${otvorena ? "" : "hero-mock__fold--zatvorena"} ${className}`}>
    <div>{children}</div>
  </div>
);

const STV = {
  kod: "STV", adresa: "Hlavná 12, Košice", suma: "4,80 €",
  meno: "Mgr. Kováčová", telefon: "+421 905 123 456", poznamka: "nechať na vrátnici",
  jedla: [["2x", "A"]],
};
const OBU = {
  kod: "OBU", adresa: "Námestie 3, Košice", suma: "7,20 €",
  meno: "Obecný úrad", telefon: "+421 911 654 321",
  jedla: [["1x", "B"], ["2x", "C"]],
};
// objednavka, ktoru manazer v animacii zapise a vodic doruci
const BOD = {
  kod: "BOD", adresa: "Bodová 8, Košice", suma: "7,40 €",
  meno: "Kaderníctvo Bella", telefon: "+421 948 222 333",
  jedla: [["1x", "A"], ["1x", "B"]],
};

const Objednavka = ({ o, trasa, novaTrasa = false, dorucena = false, className = "" }) => (
  <div className={`hero-mock__order ${className}`}>
    <div className="hero-mock__order-head">
      <span className="hero-mock__tag">{o.kod}</span>
      <span className="hero-mock__addr">{o.adresa}</span>
      <MapPin size={10} className="hero-mock__pin" />
      <strong>{o.suma}</strong>
    </div>
    <div className="hero-mock__order-body">
      <div className="hero-mock__who">
        <span>{o.meno}</span>
        <span className="hero-mock__phone-no">{o.telefon}</span>
        {o.poznamka && <em>{o.poznamka}</em>}
      </div>
      <Jedla polozky={o.jedla} />
      <div className="hero-mock__badges">
        {dorucena
          ? <span className="hero-mock__status hero-mock__status--dorucena"><CheckCircle2 size={9} /> Doručená</span>
          : <span className="hero-mock__status"><Clock size={9} /> Prijatá</span>}
        {trasa
          ? <span className={`hero-mock__route ${novaTrasa ? "hero-mock__route--nova" : ""}`}>● {trasa}</span>
          : <span className="hero-mock__route hero-mock__route--ziadna">Bez trasy</span>}
      </div>
    </div>
  </div>
);

const PLATBY = [
  { Icon: Banknote, popis: "Hotovosť" },
  { Icon: CreditCard, popis: "Kartou" },
  { Icon: FileText, popis: "Faktúra" },
  { Icon: CircleAlert, popis: "Dlh" },
];

/** Obsah okna manazera (Prehlad) v zabere, ktory urcuje `po`. */
function OknoObsah({ po, className = "" }) {
  const nova = po("nova");
  const trzba = po("trzba");

  const kpi = [
    { Icon: TrendingUp, hodnota: trzba ? 193.8 : 186.4, format: eura, popis: "Celková tržba (cash)", mod: "hlavna", puls: trzba },
    { Icon: CircleAlert, hodnota: 13.8, format: eura, popis: "Neuhradené / Dlh", mod: "dlh" },
    { Icon: ShoppingCart, hodnota: nova ? 43 : 42, format: kusy, popis: "Počet objednávok", mod: "pocet", puls: nova },
  ];

  return (
    <div className={`hero-mock__body ${className}`}>
      <div className="hero-mock__kpis">
        {kpi.map(({ Icon, hodnota, format, popis, mod, puls }) => (
          <div key={popis} className={`hero-mock__kpi hero-mock__kpi--${mod} ${puls ? "hero-mock__kpi--puls" : ""}`}>
            <div className="hero-mock__kpi-top">
              <span className="hero-mock__kpi-icon"><Icon size={12} /></span>
              <strong><Pocitadlo hodnota={hodnota} format={format} /></strong>
            </div>
            <small>{popis}</small>
          </div>
        ))}
      </div>

      <div className="hero-mock__panel">
        <div className="hero-mock__panel-head">
          <div className="hero-mock__panel-title">Zoznam objednávok</div>
          <span className={`hero-mock__new ${po("klik") && !nova ? "hero-mock__new--stlacene" : ""}`}>
            <Plus size={10} /> Nová objednávka
            <MousePointer2
              size={15}
              className={[
                "hero-mock__cursor",
                po("kurzor") && !po("trasa") ? "hero-mock__cursor--tu" : "",
                po("klik") && !nova ? "hero-mock__cursor--klik" : "",
              ].join(" ")}
            />
          </span>
        </div>
        <div className="hero-mock__summary">
          <small>Súhrn objednaných jedál</small>
          <Jedla polozky={[
            [nova ? "15x" : "14x", "A", nova],
            [nova ? "12x" : "11x", "B", nova],
            ["9x", "C"],
            ["8x", "1"],
          ]} />
        </div>

        {/* nova objednavka sa rozbali hore, posledna sa naraz zbali -- vyska okna sa nemeni */}
        <Skladacka otvorena={nova}>
          <Objednavka
            o={BOD}
            trasa={po("trasa") ? "SEVER" : null}
            novaTrasa
            dorucena={trzba}
            className={trzba ? "hero-mock__order--dorucena" : nova ? "hero-mock__order--nova" : ""}
          />
        </Skladacka>
        <Objednavka o={STV} trasa="SEVER" />
        <Skladacka otvorena={!nova}>
          <Objednavka o={OBU} trasa="JUH" />
        </Skladacka>
      </div>
    </div>
  );
}

/** Obsah obrazovky vodica (Moje zavozy) v zabere, ktory urcuje `po`. */
function MobilObsah({ po, className = "" }) {
  const vodic = po("vodic");
  const dorucene = po("dorucene");
  const platbaOtvorena = po("platba") && !dorucene;

  return (
    <div className={`hero-mock__screen-body ${className}`}>
      <div className="hero-mock__phone-bar">
        <img src={publicAsset("rozvozy_icon.png")} alt="" />
        <Menu size={14} />
      </div>
      <div className="hero-mock__phone-title">Moje závozy</div>

      <div className="hero-mock__dstats">
        <div>
          <strong>{dorucene ? 6 : 5} / {vodic ? 13 : 12}</strong>
          <small>Doručené</small>
        </div>
        <div>
          <strong className="hero-mock__green">
            <Pocitadlo hodnota={dorucene ? 45.8 : 38.4} format={eura} />
          </strong>
          <small>Vybraté z {vodic ? "93,80 €" : "86,40 €"}</small>
        </div>
        <div className="hero-mock__loaded">
          <small>Aktuálne naložené jedlá</small>
          <Jedla polozky={vodic && !dorucene
            ? [["5x", "A", true], ["4x", "B", true], ["2x", "C"]]
            : [["4x", "A", dorucene], ["3x", "B", dorucene], ["2x", "C"]]}
          />
        </div>
      </div>

      {/* zapisana objednavka BOD -- objavi sa navrchu, po doruceni sa zbali */}
      <Skladacka otvorena={vodic && !dorucene} className={dorucene ? "hero-mock__fold--pomaly" : ""}>
        <div className={`hero-mock__stop ${dorucene ? "hero-mock__stop--hotovo" : ""} ${vodic ? "hero-mock__stop--nova" : ""}`}>
          <div className="hero-mock__stop-head">
            <span className="hero-mock__tag hero-mock__tag--outline">{BOD.kod}</span>
            <span className="hero-mock__addr">{BOD.adresa}</span>
            {dorucene
              ? <span className="hero-mock__onway hero-mock__onway--hotovo">Doručené</span>
              : <span className="hero-mock__onway">Na trase</span>}
          </div>
          <div className="hero-mock__stop-body">
            <div className="hero-mock__stop-who">
              {BOD.meno} <span className="hero-mock__phone-no">{BOD.telefon}</span>
            </div>
            <div className="hero-mock__stop-meals"><Jedla polozky={BOD.jedla} /></div>
            <div className="hero-mock__prices">
              <span>Cena obeda: <b className="hero-mock__price">{BOD.suma}</b></span>
              <span>Saldo: <b className="hero-mock__saldo">0,00 €</b></span>
            </div>
          </div>
          <div className="hero-mock__deliver">
            <CreditCard size={12} /> Doručiť a zaplatiť
            <span className={`hero-mock__tap ${po("tap") && !po("platba") ? "hero-mock__tap--tu" : ""}`} />
          </div>
        </div>
      </Skladacka>

      <div className="hero-mock__stop">
        <div className="hero-mock__stop-head">
          <span className="hero-mock__tag hero-mock__tag--outline">KOV</span>
          <span className="hero-mock__addr">Hlavná 12, Košice</span>
          <span className="hero-mock__onway">Na trase</span>
        </div>
        <div className="hero-mock__stop-body">
          <div className="hero-mock__stop-who">
            Mgr. Kováčová <span className="hero-mock__phone-no">+421 900 123 456</span>
          </div>
          <div className="hero-mock__note">nechať na vrátnici</div>
          <div className="hero-mock__stop-meals"><Jedla polozky={[["2x", "B"], ["1x", "A"]]} /></div>
          <div className="hero-mock__prices">
            <span>Cena obeda: <b className="hero-mock__price">6,90 €</b></span>
            <span>Saldo: <b className="hero-mock__saldo">0,00 €</b></span>
          </div>
        </div>
        <div className="hero-mock__deliver">
          <CreditCard size={12} /> Doručiť a zaplatiť
        </div>
      </div>

      {/* dalsia zastavka -- odrezava ju spodny okraj obrazovky */}
      <div className="hero-mock__stop hero-mock__stop--next">
        <div className="hero-mock__stop-head">
          <span className="hero-mock__tag hero-mock__tag--outline">MRZ</span>
          <span className="hero-mock__addr">Priemyselná 4, Košice</span>
        </div>
        <div className="hero-mock__stop-body">
          <div className="hero-mock__stop-who">Autoservis Mráz</div>
        </div>
      </div>

      {/* platobny modal vodica (DriverPaymentModal), zmenseny na spodny panel */}
      <div className={`hero-mock__sheet ${platbaOtvorena ? "hero-mock__sheet--tu" : ""}`}>
        <div className="hero-mock__sheet-panel">
          <div className="hero-mock__sheet-title">Platba a doručenie</div>
          <div className="hero-mock__sheet-row">
            <span>{BOD.kod} · Bodová 8</span>
            <b>{BOD.suma}</b>
          </div>
          <div className="hero-mock__pay">
            {PLATBY.map(({ Icon, popis }, i) => (
              <span key={popis} className={i === 0 ? "hero-mock__pay--zvolena" : undefined}>
                <Icon size={10} /> {popis}
              </span>
            ))}
          </div>
          <div className="hero-mock__sheet-row">
            <span>Prijatá suma</span>
            <span className="hero-mock__input">
              {po("suma") ? BOD.suma : ""}
              <i className="hero-mock__caret" />
            </span>
          </div>
          <div className="hero-mock__confirm">
            Potvrdiť doručenie a platbu
            <span className={`hero-mock__tap ${po("potvrd") ? "hero-mock__tap--tu" : ""}`} />
          </div>
        </div>
      </div>
    </div>
  );
}

// posledny zaber kola -- vsetky kroky su za nami
const poKonci = () => true;
const POSLEDNY_KROK = KROKY.length - 1;

function HeroMockup() {
  const rootRef = useRef(null);
  const { kolo, krok, po } = useScenar(rootRef, KROKY, DLZKA_KOLA);

  // Novy zaciatok sa neukaze naraz: posledny zaber skonceneho kola ostane
  // navrchu a rozplynie sa do prveho zaberu noveho (ziadne prazdne okno).
  // Len ked kolo naozaj dobehlo do konca -- po preruseni (mimo obrazovky) sa
  // posledny zaber nikdy neukazal, prelinat nie je z coho.
  const odchadza = usePrelinanie(kolo, krok, POSLEDNY_KROK);

  const triedy = [
    "hero-mock",
    // na mobile je vidno bud okno, alebo telefon -- telefon pocas casti vodica
    po("vodic") && !po("naDesktop") ? "hero-mock--mobil" : "",
  ].filter(Boolean).join(" ");

  return (
    <div ref={rootRef} className={triedy} aria-hidden="true">
      {/* --- admin: Prehlad --- */}
      <div className="hero-mock__window">
        <div className="hero-mock__bar">
          <span /><span /><span />
        </div>
        <div className="hero-mock__vrstvy">
          <OknoObsah key={kolo} po={po} />
          {odchadza && <OknoObsah key={`k${odchadza.kolo}`} po={poKonci} className="hero-mock__stary" />}
        </div>
      </div>

      {/* --- vodic: Moje zavozy --- */}
      <div className="hero-mock__phone">
        <div className="hero-mock__screen">
          <div className="hero-mock__statusbar">
            <span>9:41</span>
            <span className="hero-mock__island" />
            <span className="hero-mock__sys"><Signal size={9} /><Wifi size={9} /><BatteryFull size={11} /></span>
          </div>

          <MobilObsah key={kolo} po={po} />
          {odchadza && (
            <MobilObsah key={`k${odchadza.kolo}`} po={poKonci} className="hero-mock__stary hero-mock__stary--mobil" />
          )}

          <div className="hero-mock__home" />
        </div>
      </div>
    </div>
  );
}

export default HeroMockup;
